import { PayloadAction } from "@reduxjs/toolkit";
import { authApi } from "apis/authApi";
import { LoginData, LoginSuccessType } from "constants/types/auth";
import { put, takeLatest } from "redux-saga/effects";
import { login, checkSession, updateState, logout } from "redux/auth/slice";
import { notifications } from "@mantine/notifications";
import { STORAGE_TOKEN, STORAGE_USER_INFO } from "constants/common/common";
import { AxiosResponse } from "axios";
import { CommonResponseType } from "constants/types/common";
import { LOGIN } from "routes/route.constant";

function* clearAuthState(): unknown {
  localStorage.removeItem(STORAGE_TOKEN);

  yield put({
    type: updateState.type,
    payload: { isLoggedIn: false, roles: [], permissions: [], isSessionLoading: false },
  });
}

function* markLoggedIn(): unknown {
  yield put({
    type: updateState.type,
    payload: { isLoggedIn: true },
  });
}

function* refreshAccessTokenSaga(): unknown {
  try {
    const tokenString = localStorage.getItem(STORAGE_TOKEN);

    if (!tokenString) {
      throw new Error("Lỗi không có token");
    }

    const token: LoginSuccessType = JSON.parse(tokenString);

    const _res = yield authApi.refreshToken(token.refresh_token);
    const newToken: LoginSuccessType = _res.data.data;

    localStorage.setItem(STORAGE_TOKEN, JSON.stringify(newToken));

    yield markLoggedIn();
  } catch (error) {
    yield clearAuthState();
    localStorage.removeItem(STORAGE_USER_INFO);
    notifications.show({
      title: "Phiên đăng nhập đã hết hạn",
      message: "Vui lòng đăng nhập lại",
      color: "red",
    });
  }
}

function* loginSaga(action: PayloadAction<LoginData>): unknown {
  try {
    const _res: AxiosResponse<CommonResponseType<LoginSuccessType, string>> =
      yield authApi.login(action.payload);

    if (_res.data.success) {
      const token = _res.data.data;
      localStorage.setItem(STORAGE_TOKEN, JSON.stringify(token));

      yield markLoggedIn();

      notifications.show({
        title: "Đăng nhập thành công",
        message: "Chào mừng đến với phần mềm quản lý kho",
        color: "green",
      });
    }
  } catch (error: any) {
    console.log(error);

    yield clearAuthState();
    notifications.show({
      title: "Đăng nhập không thành công",
      message: "",
      color: "red",
    });
  }
}

function* logoutSaga(): unknown {
  try {
    yield clearAuthState();
    localStorage.removeItem(STORAGE_USER_INFO);
    window.location.href = LOGIN;
    notifications.show({
      title: "Đăng xuất thành công",
      message: "Bạn đã đăng xuất khỏi hệ thống",
      color: "green",
    });
  } catch (error: any) {
    notifications.show({
      title: "Lỗi khi thực hiện đăng xuất tài khoản",
      message: "",
      color: "red",
    });
  }
}

function* checkSessionSaga(): unknown {
  yield put({
    type: updateState.type,
    payload: { isSessionLoading: true },
  });
  try {
    const _res = yield authApi.checkSession();
    if (_res.data.success) {
      const userInfo = _res.data.data;
      localStorage.setItem(STORAGE_USER_INFO, JSON.stringify(userInfo));

      yield put({
        type: updateState.type,
        payload: {
          isLoggedIn: true,
          roles: userInfo.roles ?? [],
          permissions: userInfo.permissions ?? [],
          isSessionLoading: false,
        },
      });
    }
  } catch (error: any) {
    if (!error?.success) {
      yield refreshAccessTokenSaga();
      localStorage.removeItem(STORAGE_USER_INFO);
    } else {
      yield clearAuthState();
    }
    yield put({
      type: updateState.type,
      payload: { isSessionLoading: false },
    });
  }
}

function* authSaga() {
  yield takeLatest(login.type, loginSaga);
  yield takeLatest(logout.type, logoutSaga);
  yield takeLatest(checkSession.type, checkSessionSaga);
}

export default authSaga;
